import classNames from "classnames";
import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useEffect, useRef, useState } from "react";
import SVG from "react-inlinesvg";
import TextareaAutosize from "react-textarea-autosize";
import { AI_VISIBILITY } from "../../../../src/shared/enums.js";
import { BLANK_TIPTAP_JSON, MAX_NUM_CHARACTERS_PER_FIELD_LABEL } from "../../../../src/shared/schemas/CharacterSchema";
import useOnClickOutside from "../../hooks/useOnClickOutside";
import { useAppStore } from "../../stores/appStore.js";
import { truncateString } from "../../Utils";
import ElementVisibilityController from "../ElementVisibilityController";
import IconButton from "../IconButton/index.jsx";
import { RestorableTiptapField } from "../TiptapField";
import FieldLoading from "./FieldLoading/index.js";
import { CustomFieldActionMenuContainer, CustomFieldContainer } from "./styles";
import { SudoButton } from "../../sudokit/components/SudoButton";
import { cn } from "src/lib/cn.js";

const CustomFieldActionMenu = ({ visible, setVisible, field: _field, index, setEntity, inputRef, setIsFlat, isEmpty, showGuidance: _showGuidance, children, showActions, anchor = "right" }) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const ref = useRef();

  useOnClickOutside(ref, () => {
    setVisible(false);
    setShowDeleteConfirm(false);
    setIsFlat(false);
  });

  const handleDelete = () => {
    setEntity((draft) => {
      draft.customFields = draft.customFields.filter((_, i) => i !== index);
    });
    setShowDeleteConfirm(false);
    setVisible(false);
  };
  const actions = {
    rename: {
      label: "Rename",
      icon: (
        <SVG
          src="/assets/pencil.svg"
          aria-hidden="true"
        />
      ),
      onClick: () => {
        setTimeout(() => inputRef.current?.focus(), 0);
        setVisible(false);
      },
    },
    delete: {
      label: "Delete",
      icon: (
        <SVG
          src="/assets/delete.svg"
          aria-hidden="true"
        />
      ),
      onClick: (e) => {
        if (isEmpty) {
          handleDelete();
        } else {
          e.stopPropagation();
          e.preventDefault();
          setShowDeleteConfirm(true);
        }
      },
    },
  };
  if (isEmpty) {
    delete actions.rewrite;
  }

  return (
    <CustomFieldActionMenuContainer
      className="item-menu"
      aria-label={`${visible ? "close" : "open"} actions menu`}
    >
      {children}
      {visible && (
        <div
          className={classNames("custom-field-action-menu", { visible }, `anchor-${anchor}`)}
          ref={ref}
        >
          <div className={classNames("custom-field-action-menu__inner")}>
            {showActions &&
              (!showDeleteConfirm ? (
                <div className="actions">
                  {Object.keys(actions).map((action) => (
                    <div
                      onClick={(e) => actions[action].onClick(e)}
                      key={action}
                      className={classNames("menu-item", action)}
                    >
                      <div className="icon">{actions[action].icon}</div>
                      <div className="label">{actions[action].label}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="delete-message-container">
                  <div className="delete-heading">Are you sure?</div>
                  <div className="delete-message">
                    You've got words here. They'll be gone forever (like, <span>gone</span> gone).
                  </div>
                  <div
                    className="confirm"
                    role="button"
                    tabIndex="0"
                    aria-label="Confirm delete this custom field"
                    onClick={() => handleDelete()}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        handleDelete();
                      }
                    }}
                  >
                    Yes, I'm sure
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}
    </CustomFieldActionMenuContainer>
  );
};

export default function CustomField({
  field,
  index,
  setEntity,
  readOnly,
  isRestoring,
  isFlat,
  setIsFlat,
  regenerateTrait,
  autoFocus,
  loading,
  searchableId,
  searchableName,
  onBlur,
  userId,
  projectId,
  diffValue,
  diffFieldKey,
  onDiffFieldChange,
  onClearDiff,
  characterName,
  ctaEnabled,
  ctaShimmer,
  onCtaTrigger,
  globalCtaActive,
  fieldLimitExceeded = false,
}) {
  const [label, setLabel] = useState(field.label);
  const [actionMenuVisible, setActionMenuVisible] = useState(false);
  const [fieldFocussed, setFieldFocussed] = useState(false);
  const [guidanceVisible, setGuidanceVisible] = useState(false);
  const [guidance, setGuidance] = useState("");
  const [regenerating, setRegenerating] = useState(false);
  const [isHoveredOverField, setIsHoveredOverField] = useState(false);

  const handleMouseEnter = useCallback(() => setIsHoveredOverField(true), []);
  const handleMouseLeave = useCallback(() => setIsHoveredOverField(false), []);

  const showActionMenu = (targetValue) => {
    setActionMenuVisible(targetValue);
    setIsFlat(targetValue);
  };

  const showGuidance = (targetValue) => {
    setGuidanceVisible(targetValue);
    setIsFlat(targetValue);
  };

  const inputRef = useRef(null);
  const isEmpty = !field.value || JSON.stringify(field.value) === JSON.stringify(BLANK_TIPTAP_JSON);
  const handleRegenerate = async (guidance) => {
    setRegenerating(true);
    await regenerateTrait(field.id, field.label, guidance, isEmpty);
    setRegenerating(false);
  };

  const handleSaveLabel = () => {
    setEntity((draft) => {
      if (draft.customFields[index]) {
        draft.customFields[index].label = label;
      }
    });
  };

  const setFieldVisibility = (visibility) => {
    setEntity((draft) => {
      draft.customFields[index].aiVisibility = visibility;
    });
    showActionMenu(false);
  };

  const fillInProps = {
    icon: (
      <SVG
        src="/assets/AI-sparkle.svg"
        aria-hidden="true"
        height={24}
        width={24}
      />
    ),
    iconSrc: "/assets/AI-sparkle.svg",
    label: "Generate...",
    loadingText: "Generating...",
  };
  const regenerateProps = {
    icon: (
      <SVG
        src="/assets/rewrite-icon.svg"
        aria-hidden="true"
        height={24}
        width={24}
      />
    ),
    iconSrc: "/assets/rewrite-icon.svg",
    label: "Rewrite...",
    loadingText: "Rewriting...",
  };

  const traitRegenProps = regenerateTrait && (isEmpty ? fillInProps : regenerateProps);
  const showFieldTools = fieldFocussed || guidanceVisible || actionMenuVisible;

  const isMac = useAppStore((state) => state.isMac);

  const guidanceRef = useRef();
  useOnClickOutside(guidanceRef, () => {
    showGuidance(false);
  });
  const guidanceInputRef = useRef();
  useEffect(() => {
    if (guidanceVisible && guidanceInputRef.current) {
      guidanceInputRef.current.focus();
    }
  }, [guidanceVisible]);

  const handleRegenerateTraitSubmit = (e) => {
    e.preventDefault();
    showGuidance(false);
    handleRegenerate(guidance);
  };

  const handleGuidanceKeyPress = (e) => {
    // useKeyboardShortcuts hook?
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleRegenerateTraitSubmit(e);
    }
  };

  const guidancePlaceholder = isEmpty ? `Tell Sudowrite how to fill in "${field.label}"` : `Tell Sudowrite how to rewrite "${field.label}"`;

  return (
    <CustomFieldContainer
      className={cn("field", fieldLimitExceeded && "field-limit-exceeded")}
      key={`container-${field.id}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="custom-field-header">
        <input
          key={`label-${field.id}`}
          type="text"
          value={label}
          maxLength={MAX_NUM_CHARACTERS_PER_FIELD_LABEL}
          placeholder="Field Label..."
          onChange={(e) => {
            setLabel(e.target.value);
          }}
          onFocus={(e) => e.target.select()}
          onBlur={handleSaveLabel}
          ref={inputRef}
          className={cn("transition-opacity", loading && "opacity-50")}
          disabled={readOnly || loading}
          autoFocus={autoFocus}
          aria-label={`${label || "Custom field"} label`}
        />
        <CustomFieldActionMenu
          visible={actionMenuVisible}
          setVisible={showActionMenu}
          field={field}
          index={index}
          setEntity={setEntity}
          inputRef={inputRef}
          setIsFlat={setIsFlat}
          showGuidance={showGuidance}
          isEmpty={isEmpty}
          showActions={true}
          anchor="right"
        >
          <div className="hotdog-dots">
            {readOnly ? null : (
              <IconButton
                onClick={() => {
                  showActionMenu((visible) => !visible);
                  setIsFlat(true);
                }}
                src="/assets/three-dots.svg"
                disabled={readOnly}
                ariaLabel="custom field actions menu"
              />
            )}
          </div>
        </CustomFieldActionMenu>
      </div>
      <AnimatePresence>
        {!ctaEnabled && guidanceVisible && (
          <motion.div
            initial={{ opacity: 0, scaleY: 0.9, translateY: "100%" }}
            animate={{ opacity: 1, scaleY: 1, translateY: "100%" }}
            exit={{ opacity: 0, scaleY: 0.9, translateY: "100%" }}
            transition={{ duration: 0.15 }}
            ref={guidanceRef}
            className="guidance-container"
          >
            <form>
              <div className="input-container">
                <p className="guidance-header">Custom Instructions (optional)</p>
                <TextareaAutosize
                  value={guidance}
                  onChange={(e) => setGuidance(e.target.value)}
                  placeholder={truncateString(guidancePlaceholder, 50)}
                  maxRows={guidance.length === 0 ? 1 : undefined}
                  rows={1}
                  ref={guidanceInputRef}
                  onKeyDown={handleGuidanceKeyPress}
                  aria-label={`Custom instructions for ${field.label || "custom field"}`}
                />
              </div>
              <div className="guidance-button-container">
                <button
                  className="go-button-container"
                  aria-label="Go"
                  onClick={handleRegenerateTraitSubmit}
                >
                  Go
                  <div
                    className="keyboard-shortcut-container"
                    aria-label={`Press ${isMac ? "command" : "control"} plus enter to generate a character with guidance`}
                    role="button"
                  >
                    <SVG
                      src={isMac ? "/assets/keyboard-shortcuts/command-key.svg" : "/assets/keyboard-shortcuts/control-key-pc.svg"}
                      className="keyboard-shortcut"
                      aria-hidden="true"
                    />
                    <SVG
                      src="/assets/keyboard-shortcuts/enter-key.svg"
                      className="keyboard-shortcut"
                      aria-hidden="true"
                    />
                  </div>
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
      {!regenerating && !ctaShimmer && (
        <div className={classNames("editor-options-container", { flat: isFlat })}>
          {!readOnly && (
            <ElementVisibilityController
              isVisible={field.aiVisibility !== AI_VISIBILITY.HIDDEN}
              onClick={() => setFieldVisibility(field.aiVisibility === AI_VISIBILITY.HIDDEN ? AI_VISIBILITY.AUTO : AI_VISIBILITY.HIDDEN)}
              small={true}
              hoverTipOffsetX={isEmpty ? 0 : 50}
              hoverTipOffsetY={5}
              shouldShow={isHoveredOverField}
            />
          )}
        </div>
      )}
      {!regenerating && !ctaShimmer && (
        <motion.div
          className="field-container"
          transition={{ duration: 0.2 }}
          initial={false}
          exit={false}
          onClickCapture={() => {
            if (diffValue && onClearDiff && diffFieldKey) {
              onClearDiff(diffFieldKey);
            }
          }}
        >
          <RestorableTiptapField
            key={diffValue ? 'diff' : 'clean'}
            loading={loading}
            value={diffValue || field.value}
            fieldLimitExceeded={fieldLimitExceeded}
            onChange={(json) => {
              if (diffValue) return;
              setEntity((draft) => {
                if (draft.customFields[index]) {
                  draft.customFields[index].value = json;
                }
              });
            }}
            onFocus={() => {
              if (diffValue && onDiffFieldChange && diffFieldKey) {
                onDiffFieldChange(diffFieldKey);
              }
              setFieldFocussed(true);
            }}
            onBlur={() => {
              // this is in a timeout so that the click event can make it
              // through to the action menu before it closes
              setTimeout(() => {
                setFieldFocussed(false);
              }, 200);
              // Call parent's onBlur handler if provided (for flushing debounced saves)
              if (onBlur) {
                onBlur();
              }
            }}
            readOnly={readOnly}
            restoring={isRestoring}
            searchableId={searchableId}
            searchableName={searchableName}
            userId={userId}
            projectId={projectId}
            ariaLabel={`${field.label || "Custom field"} value for ${characterName || "character"}`}
          />
          <AnimatePresence mode="popLayout">
            {showFieldTools && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
                className="field-hints-container"
              >
                {!readOnly && traitRegenProps && !(ctaEnabled && globalCtaActive) && (
                  <SudoButton
                    onClick={() =>
                      ctaEnabled && onCtaTrigger ? onCtaTrigger(field.id, field.label, isEmpty) : showGuidance(true)
                    }
                    variant="tertiary"
                    className="mb-2 ml-2"
                    leftIcon={traitRegenProps.iconSrc}
                  >
                    {traitRegenProps.label}
                  </SudoButton>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}
      {(regenerating || ctaShimmer) && (
        <FieldLoading
          content={field.value}
          loadingText={ctaShimmer ? null : traitRegenProps?.loadingText}
          userId={userId}
          projectId={projectId}
        />
      )}
    </CustomFieldContainer>
  );
}
